import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { HardHat, LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { useState } from "react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `px-3 py-2 text-sm tracking-wide uppercase font-bold transition-colors ${
      isActive ? "text-amber-500" : "text-neutral-300 hover:text-amber-500"
    }`;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col">
      <header className="sticky top-0 z-40 border-b border-neutral-800 bg-neutral-950/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
            <div className="w-9 h-9 bg-amber-500 grid place-items-center">
              <HardHat className="w-5 h-5 text-neutral-950" strokeWidth={2.5} />
            </div>
            <div className="display text-2xl leading-none">
              <span className="text-neutral-100">SITE</span>
              <span className="text-amber-500">CREWS</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/jobs" data-testid="nav-jobs" className={linkClass}>Jobs</NavLink>
            <NavLink to="/workers" data-testid="nav-workers" className={linkClass}>Crew</NavLink>
            <NavLink to="/referrals" data-testid="nav-referrals" className={linkClass}>Earn €10</NavLink>
            {user && (
              <NavLink to="/dashboard" data-testid="nav-dashboard" className={linkClass}>Dashboard</NavLink>
            )}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                {user.role === "employer" && (
                  <Link
                    to="/jobs/new"
                    data-testid="nav-post-job"
                    className="px-4 py-2 bg-amber-500 text-neutral-950 text-sm font-bold uppercase tracking-wide hover:bg-amber-400 transition-colors"
                  >
                    Post Job
                  </Link>
                )}
                {user.role === "worker" && (
                  <Link
                    to="/workers/new"
                    data-testid="nav-post-availability"
                    className="px-4 py-2 bg-amber-500 text-neutral-950 text-sm font-bold uppercase tracking-wide hover:bg-amber-400 transition-colors"
                  >
                    Post Availability
                  </Link>
                )}
                <div className="flex items-center gap-3 pl-3 border-l border-neutral-800">
                  <div className="text-right leading-tight">
                    <div className="text-xs text-neutral-400 uppercase">{user.role}</div>
                    <div className="text-sm font-bold">{user.name}</div>
                  </div>
                  <button
                    data-testid="logout-btn"
                    onClick={onLogout}
                    className="p-2 border border-neutral-800 hover:border-amber-500 hover:text-amber-500 transition-colors"
                    aria-label="Log out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="px-4 py-2 text-sm font-bold uppercase tracking-wide text-neutral-300 hover:text-amber-500 transition-colors"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register"
                  className="px-4 py-2 bg-amber-500 text-neutral-950 text-sm font-bold uppercase tracking-wide hover:bg-amber-400 transition-colors"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 border border-neutral-800"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-neutral-800 bg-neutral-950">
            <div className="px-4 py-3 flex flex-col gap-2">
              <NavLink to="/jobs" onClick={() => setOpen(false)} className={linkClass}>Jobs</NavLink>
              <NavLink to="/workers" onClick={() => setOpen(false)} className={linkClass}>Crew</NavLink>
              <NavLink to="/referrals" onClick={() => setOpen(false)} className={linkClass}>Earn €10</NavLink>
              {user && <NavLink to="/dashboard" onClick={() => setOpen(false)} className={linkClass}>Dashboard</NavLink>}
              {!user && (
                <>
                  <NavLink to="/login" onClick={() => setOpen(false)} className={linkClass}>Log in</NavLink>
                  <NavLink to="/register" onClick={() => setOpen(false)} className={linkClass}>Sign up</NavLink>
                </>
              )}
              {user && user.role === "employer" && (
                <Link to="/jobs/new" onClick={() => setOpen(false)} className="px-3 py-2 bg-amber-500 text-neutral-950 text-sm font-bold uppercase">Post Job</Link>
              )}
              {user && user.role === "worker" && (
                <Link to="/workers/new" onClick={() => setOpen(false)} className="px-3 py-2 bg-amber-500 text-neutral-950 text-sm font-bold uppercase">Post Availability</Link>
              )}
              {user && (
                <button data-testid="logout-btn-mobile" onClick={() => { setOpen(false); onLogout(); }} className="px-3 py-2 border border-neutral-800 text-sm font-bold uppercase text-left">Log out</button>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-neutral-800 mt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex flex-col md:flex-row justify-between gap-4 text-xs text-neutral-500 uppercase tracking-widest">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="w-3 h-3" />
            SiteCrews · Ireland · Trades Marketplace
          </div>
          <div className="flex items-center gap-4">
            <Link to="/terms" data-testid="footer-terms" className="hover:text-amber-500 transition-colors">Terms</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-amber-500 transition-colors">Privacy</Link>
            <Link to="/cookies" data-testid="footer-cookies" className="hover:text-amber-500 transition-colors">Cookies</Link>
          </div>
          <div>Dublin · Meath · Louth · Monaghan</div>
        </div>
      </footer>
    </div>
  );
}
